import { icons } from '@/components/icons';
import { getProduct } from '@/api/products';
import { HojApiError } from '@/api/client';
import { selectionStore } from '@/lib/store';
import { escapeHtml, formatDate, formatPrice } from '@/lib/format';
import { nextBookableAvailability, todayIso } from '@/lib/availability';
import type { Product } from '@/api/types';
import type { Screen } from '@/router/router';

type Tab = 'overview' | 'itinerary' | 'venue';

function durationLabel(days?: number | null): string {
  if (!days || days < 1) return 'TRIP';
  if (days === 1) return '1 GIORNO';
  return `${days} GIORNI`;
}

/* Lightweight markdown-ish to HTML (the API ships description as markdown with
 * **bold** and \n paragraphs). Only what we actually see in the data set is
 * handled; everything else is escaped.
 */
function renderDescription(md: string | null | undefined): string {
  if (!md) return '';
  const paragraphs = md
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  return paragraphs
    .map((p) => {
      const escaped = escapeHtml(p).replace(/\n/g, '<br/>');
      const bolded = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      return `<p>${bolded}</p>`;
    })
    .join('');
}

function renderItinerary(p: Product): string {
  const list = p.availabilities ?? [];
  if (list.length === 0) {
    return '<p>Calendario delle partenze in aggiornamento. Contatta il supporto per richieste personalizzate.</p>';
  }
  const today = todayIso();
  const upcoming = list
    .filter(
      (a) =>
        (a.status ?? '').toLowerCase() === 'bookable' &&
        (a.startDate ?? '') >= today,
    )
    .slice(0, 6);
  if (upcoming.length === 0) {
    return '<p>Nessuna data prenotabile in questo momento.</p>';
  }
  return `
    <h3>Prossime partenze</h3>
    <ul class="inc-list" style="list-style:none;padding:0;margin:0;">
      ${upcoming
        .map((a) => {
          const from = a.startDate ? formatDate(a.startDate) : '';
          const to = a.endDate ? formatDate(a.endDate) : '';
          return `
            <li style="display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--gray-100);font-size:13px;color:var(--gray-700);">
              <span>${from}${to ? ` – ${to}` : ''}</span>
              <span style="color:var(--wp-cyan-dk);font-weight:700;">Prenotabile</span>
            </li>`;
        })
        .join('')}
    </ul>
  `;
}

function renderVenue(p: Product): string {
  return `
    <p>${
      p.venueId
        ? `Struttura partner ID <code>${escapeHtml(p.venueId)}</code>. Dettagli completi sul sito di prenotazione.`
        : 'Informazioni sulla struttura in caricamento.'
    }</p>`;
}

export function createProductDetailScreen(): Screen {
  let rootEl!: HTMLElement;
  let scrollEl!: HTMLElement;
  let footerEl!: HTMLElement;
  let onBack!: () => void;
  let getParams!: () => Record<string, string>;
  let goToCustomize!: (productId: string) => void;

  let currentProduct: Product | undefined;
  let activeTab: Tab = 'overview';
  let loading = false;
  let error: string | undefined;

  function rerender(): void {
    if (loading) {
      scrollEl.innerHTML = `<div class="state-msg"><div class="spinner"></div><p>Caricamento dettaglio…</p></div>`;
      footerEl.style.display = 'none';
      return;
    }
    const heroImg = rootEl.querySelector<HTMLImageElement>('[data-role="hero-img"]');
    if (heroImg) {
      if (currentProduct?.coverUrl) {
        heroImg.src = currentProduct.coverUrl;
        heroImg.style.display = 'block';
        heroImg.onerror = () => {
          heroImg.style.display = 'none';
        };
      } else {
        heroImg.removeAttribute('src');
        heroImg.style.display = 'none';
      }
    }

    if (error || !currentProduct) {
      scrollEl.innerHTML = `
        <div class="state-msg">
          <h3>Impossibile caricare il prodotto</h3>
          <p>${escapeHtml(error ?? 'Riprova più tardi.')}</p>
          <button class="btn btn-navy" data-action="retry" type="button">Riprova</button>
        </div>`;
      footerEl.style.display = 'none';
      scrollEl.querySelector<HTMLButtonElement>('[data-action="retry"]')?.addEventListener(
        'click',
        () => {
          void reload();
        },
      );
      return;
    }

    const p = currentProduct;
    const avail = nextBookableAvailability(p);
    const price = formatPrice(p.price, p.currency ?? 'EUR');
    const dur = durationLabel(p.defaultDurationInDays);
    const periodLabel = avail?.startDate
      ? `dal ${formatDate(avail.startDate)}`
      : p.minDate && p.maxDate
        ? `${formatDate(p.minDate)} – ${formatDate(p.maxDate)}`
        : '';

    scrollEl.innerHTML = `
      <div class="pd-info">
        <div class="pd-dur">${dur}</div>
        <div class="pd-title">${escapeHtml(p.title)}</div>
        <div class="pd-meta">
          ${periodLabel ? `<div class="pd-meta-item">${icons.calendar()} ${escapeHtml(periodLabel)}</div>` : ''}
          ${p.minPax || p.maxPax ? `<div class="pd-meta-item">${icons.users()} ${p.minPax ?? ''}${p.minPax && p.maxPax ? '-' : ''}${p.maxPax ?? ''} pax</div>` : ''}
        </div>
      </div>

      <div class="pd-tabs" data-role="tabs">
        <button class="pd-tab ${activeTab === 'overview' ? 'on' : ''}" data-tab="overview" type="button">Panoramica</button>
        <button class="pd-tab ${activeTab === 'itinerary' ? 'on' : ''}" data-tab="itinerary" type="button">Calendario</button>
        <button class="pd-tab ${activeTab === 'venue' ? 'on' : ''}" data-tab="venue" type="button">Struttura</button>
      </div>

      <div class="pd-body" data-role="tab-body">
        ${
          activeTab === 'overview'
            ? `${p.shortDescription ? `<p><strong>${escapeHtml(p.shortDescription)}</strong></p>` : ''}${renderDescription(p.description)}`
            : activeTab === 'itinerary'
              ? renderItinerary(p)
              : renderVenue(p)
        }
      </div>
    `;

    scrollEl.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((b) => {
      b.addEventListener('click', () => {
        activeTab = (b.dataset.tab as Tab) ?? 'overview';
        rerender();
      });
    });

    footerEl.style.display = '';
    footerEl.innerHTML = `
      <div class="pd-price-block">
        <span class="pd-price-from">da</span>
        <span class="pd-price-amt">${price}</span>
      </div>
      <button class="btn btn-pill-lime" data-action="customize" type="button">
        <span>Personalizza</span>
        <span class="btn-pill-arrow" aria-hidden="true">→</span>
      </button>
    `;
    footerEl
      .querySelector<HTMLButtonElement>('[data-action="customize"]')
      ?.addEventListener('click', () => {
        // Always start the customize flow on the first FUTURE bookable date
        // (the API rejects past startDates with RESERVATION_PERIOD_ERROR).
        const next = nextBookableAvailability(p);
        const today = todayIso();
        const startDate =
          next?.startDate && next.startDate >= today
            ? next.startDate
            : selectionStore.get().startDate >= today
              ? selectionStore.get().startDate
              : today;
        selectionStore.set({ startDate });
        goToCustomize(p.id);
      });
  }

  async function reload(): Promise<void> {
    const params = getParams();
    const productIdParam = params.productId;
    const stored = selectionStore.get().product;
    const idToFetch = productIdParam ?? stored?.id;

    if (!idToFetch) {
      error = 'Nessun prodotto selezionato.';
      rerender();
      return;
    }

    loading = true;
    error = undefined;
    if (stored && stored.id === idToFetch) {
      // Show cached data immediately while refreshing in background.
      currentProduct = stored;
      loading = false;
      rerender();
    } else {
      currentProduct = undefined;
      rerender();
    }

    try {
      const fresh = await getProduct(idToFetch);
      currentProduct =
        stored && stored.id === idToFetch
          ? { ...stored, ...fresh }
          : fresh;
      selectionStore.set({ product: currentProduct });
    } catch (err) {
      // Fall back to the cached version from the listing if the detail call
      // fails (e.g. transient network issue).
      if (stored && stored.id === idToFetch) {
        currentProduct = stored;
      }
      if (err instanceof HojApiError && err.status >= 500) {
        error = err.message;
      } else if (!currentProduct) {
        error = err instanceof Error ? err.message : 'Errore di rete';
      }
    } finally {
      loading = false;
      rerender();
    }
  }

  return {
    id: 'product-detail',
    async mount({ el, back, go, params }) {
      rootEl = el;
      onBack = back;
      getParams = params;
      goToCustomize = (id) => go('customize', { params: { productId: id } });
      rootEl.id = 'wpd';

      rootEl.innerHTML = `
        <div class="pd-hero" data-role="hero">
          <div class="pd-hero-fallback"></div>
          <img class="pd-hero-img" data-role="hero-img" alt="" referrerpolicy="no-referrer" style="display:none;" />
          <div class="pd-hero-overlay"></div>
          <div class="pd-top-nav">
            <button class="hero-ibtn" data-action="back" aria-label="Indietro">${icons.back(18, '#fff')}</button>
            <div class="pd-top-nav-right">
              <button class="hero-ibtn" aria-label="Salva">${icons.heart(17, '#fff')}</button>
              <button class="hero-ibtn" aria-label="Condividi">${icons.share(17, '#fff')}</button>
            </div>
          </div>
        </div>
        <div class="pd-scroll" data-role="scroll"></div>
        <div class="pd-cta-footer" data-role="footer" style="display:none;"></div>
      `;

      scrollEl = rootEl.querySelector<HTMLElement>('[data-role="scroll"]')!;
      footerEl = rootEl.querySelector<HTMLElement>('[data-role="footer"]')!;
      rootEl
        .querySelector<HTMLButtonElement>('[data-action="back"]')
        ?.addEventListener('click', () => onBack());
    },
    async onEnter() {
      activeTab = 'overview';
      scrollEl.scrollTo({ top: 0, behavior: 'auto' });
      await reload();
    },
  };
}
